import { isDefined } from '@priva/utilities/predicate';

import { ProductBoxRegistration } from '../components/product-box/models';
import { Activity } from '../features/activity-selector/models';
import { ResultDeterminationType } from '../models';
import { isAmountResultValidOrEmpty } from './amount-result.utils';
import { arrayEqual } from './array.utils';

const compareProductBoxRegistrations = (a: ProductBoxRegistration, b: ProductBoxRegistration) =>
    a.productBoxNumber - b.productBoxNumber;

export function sortProductBoxRegistrations(list: ProductBoxRegistration[]) {
    return [...list].sort(compareProductBoxRegistrations);
}

export function resetProductBoxRegistrationAmounts(productBoxRegistrations: ProductBoxRegistration[]) {
    return productBoxRegistrations.map(
        ({ productBoxNumber }): ProductBoxRegistration => ({
            amount: undefined,
            productBoxNumber,
        }),
    );
}

export function getProductBoxRegistrationByBoxNumber(
    productBoxRegistrations: ProductBoxRegistration[],
    productBoxNumber: number,
) {
    return productBoxRegistrations.find((registration) => registration.productBoxNumber === productBoxNumber);
}

export function areProductBoxRegistrationsEqual(
    a: ProductBoxRegistration | undefined,
    b: ProductBoxRegistration | undefined,
) {
    return a === b || (a?.productBoxNumber === b?.productBoxNumber && a?.amount === b?.amount);
}

export function areProductBoxRegistrationArraysEqual(
    a: ProductBoxRegistration[],
    b: ProductBoxRegistration[],
) {
    return arrayEqual(a, b, compareProductBoxRegistrations);
}

/* Validation differs by ResultDeterminationType
    byAmountAndProductBox requires at least one amount to be more than or equals to 0 (not null/undefined)
    ByContainerAndProductBox requires all amount to be null
 */
export function areProductBoxRegistrationsAmountValid(
    productBoxRegistrations: ProductBoxRegistration[],
    resultDeterminationType?: ResultDeterminationType,
) {
    return resultDeterminationType === ResultDeterminationType.ByContainerAndProductBox
        ? productBoxRegistrations.every(({ amount }) => amount === null)
        : productBoxRegistrations.some(({ amount }) => isDefined(amount) && amount >= 0);
}

export function areProductBoxRegistrationsValid(
    productBoxRegistrations: ProductBoxRegistration[],
    activity: Activity | undefined,
) {
    return (
        productBoxRegistrations.length > 0 &&
        productBoxRegistrations.every((productBoxRegistration) =>
            isProductBoxRegistrationValid({ productBoxRegistration, activity }),
        ) &&
        areProductBoxRegistrationsAmountValid(productBoxRegistrations, activity?.resultDeterminationType)
    );
}

export function isProductBoxRegistrationValid({
    productBoxRegistration: { productBoxNumber, amount },
    activity,
}: {
    productBoxRegistration: ProductBoxRegistration;
    activity: Activity | undefined;
}) {
    return productBoxNumber !== undefined && isAmountResultValidOrEmpty({ amountResult: amount, activity });
}
